import React, { useState, useEffect, useRef } from "react";
import QuestionItem from "../components/QuestionItem";
import QuestionsAnswersArray from "../components/QuestionsAnswersArray";
import { CButton } from "@coreui/react";
import useQuestions from "../hooks/useQuestions";
import { ReactComponent as PlusIcon } from "../assets/images/icons/plus.svg";
import { ReactComponent as BinIcon } from "../assets/images/icons/bin.svg";
import { Tooltip } from "react-tooltip";
import { FormArray, FormBuilder } from "react-reactive-form";
import useModalWindow from "../hooks/useModalWindow";
import {
  calculateInitialPdBlockIds,
  addPdBlock,
  removePdBlock,
} from "./utils/pdUtils";

const PDQuestions = ({
  globalId,
  wizardForm,
  updatePdTreatmentGroupsComparisonAdditionalData,
}) => {
  const [pdBlockIds, setPdBlockIds] = useState(() =>
    calculateInitialPdBlockIds(wizardForm)
  );
  const nextBlockIdRef = useRef(Math.max(...pdBlockIds, 0) + 1);
  const { questionsDataSaved } = useQuestions();
  const { modalWindow } = useModalWindow();

  // Determine base question index (1 if PK exists, otherwise 1)
  // Since we're on a separate page, it's always 1
  const baseQuestionIndex = "1";

  // const dash = " - ";
  const dash = "";

  // Update PD block IDs when form values are restored
  useEffect(() => {
    const newIds = calculateInitialPdBlockIds(wizardForm);
    const currentKey = pdBlockIds.join(",");
    const newKey = newIds.join(",");
    if (newKey !== currentKey) {
      setPdBlockIds(newIds);
      nextBlockIdRef.current = Math.max(...newIds, 0) + 1;
    }
  }, [
    wizardForm.value.qPdStdAnalyses,
    wizardForm.value.qPdAnalyses,
    wizardForm.controls,
  ]);

  // Initialize controls for existing blocks
  useEffect(() => {
    const isPdEnabled =
      wizardForm.controls.qPdStdEnabled?.value === "true" ||
      wizardForm.controls.qPdCompEnabled?.value === "true" ||
      questionsDataSaved?.wizardForm?.qPdStdEnabled === "true" ||
      questionsDataSaved?.wizardForm?.qPdCompEnabled === "true";
    if (isPdEnabled) {
      pdBlockIds.forEach(blockId => {
        const suffix = blockId > 1 ? `_${blockId}` : "";

        const controlsToEnsure = [
          { name: `qPdTable${suffix}`, defaultValue: "" },
          { name: `qPdPopulation${suffix}`, defaultValue: "" },
          { name: `qPdComparative${suffix}`, defaultValue: "false" },
          { name: `qPdCompName${suffix}`, defaultValue: "" },
          { name: `qPdSummaryType${suffix}`, defaultValue: "false" },
          { name: `qPdParameters${suffix}`, defaultValue: [] },
          { name: `qPdTreatmentGroups${suffix}`, defaultValue: [] },
          { name: `qPdComparisonPairs${suffix}`, defaultValue: [] },
          { name: `qPdDoseInteraction${suffix}`, defaultValue: "false" },
          { name: `qPdColumnHeaders${suffix}`, defaultValue: [] },
          { name: `qPdTimepoints${suffix}`, defaultValue: [] },
          { name: `qPdStatTables${suffix}`, defaultValue: "" },
        ];

        controlsToEnsure.forEach(({ name, defaultValue }) => {
          if (!wizardForm.contains(name)) {
            if (
              name.startsWith("qPdParameters") ||
              name.startsWith("qPdTreatmentGroups") ||
              name.startsWith("qPdComparisonPairs") ||
              name.startsWith("qPdColumnHeaders") ||
              name.startsWith("qPdTimepoints")
            ) {
              wizardForm.addControl(name, new FormArray([]));
            } else {
              wizardForm.addControl(name, FormBuilder.control(defaultValue));
            }
          }
        });
      });
    }
  }, [
    wizardForm,
    pdBlockIds,
    questionsDataSaved?.wizardForm?.qPdStdEnabled,
    questionsDataSaved?.wizardForm?.qPdCompEnabled,
  ]);

  const handleAddBlock = e => {
    console.log(e);
    const newId = nextBlockIdRef.current;
    nextBlockIdRef.current += 1;
    addPdBlock(wizardForm, newId);
    setPdBlockIds(prev => [...prev, newId]);
  };

  const handleRemoveBlock = async (e, displayIndex) => {
    if (e) e.preventDefault();
    if (pdBlockIds.length <= 1) return;
    const confirmed = await modalWindow({
      title: "Remove Block",
      message: "Do you want to remove this block of questions?",
      confirmButtonLabel: "Yes",
    });
    if (!confirmed) return;
    const blockId = pdBlockIds[displayIndex];
    removePdBlock(wizardForm, blockId);
    setPdBlockIds(prev => prev.filter((_, i) => i !== displayIndex));
  };

  return (
    <>
      <QuestionItem
        questionIndex={baseQuestionIndex + ""}
        questionName={"qPdStdEnabled"}
        activator={"true"}
        questionInputType={"radio"}
        questionInputChoice={"yes_no"}
        globalId={globalId}
      />
      <QuestionItem
        questionIndex={"2"}
        questionName={"qPdCompEnabled"}
        activator={"true"}
        questionInputType={"radio"}
        questionInputChoice={"yes_no"}
        globalId={globalId}
      />

      {/* PD analysis blocks */}
      {(wizardForm.controls.qPdStdEnabled?.value === "true" ||
        wizardForm.controls.qPdCompEnabled?.value === "true") && (
        <>
          {pdBlockIds.map((blockId, displayIndex) => {
            const suffix = blockId > 1 ? `_${blockId}` : "";
            const isFirstBlock = displayIndex === 0;
            const blockNumber = displayIndex + 1;

            // Whole-number indexing: 1, 2, 3, 4, ... ; extra blocks use "2 - (blockNumber)." etc.
            const q = n =>
              isFirstBlock ? `${n}` : `${n}${dash}(${blockNumber})`;
            const questionIndex1a = q(2);
            const questionIndex1b = q(3);
            const questionIndex1c = q(4);
            const questionIndex1ci = q(5);
            const questionIndex1cii = q(6);
            const questionIndex1ciii = q(7);
            const questionIndex1civ = q(8);
            const questionIndex1cv = q(9);
            const questionIndex1ciNonComp = q(5);
            const questionIndex1d = q(11);
            const questionIndex1e = q(12);

            // Get the comparative value for this block
            const qPdComparativeValue =
              wizardForm.controls[`qPdComparative${suffix}`]?.value === "true";

            const qPdComparativeValueWithoutStatistics =
              wizardForm.controls[`qPdComparative${suffix}`]?.value ===
                "true" &&
              wizardForm.controls[`qPdSummaryType${suffix}`]?.value === "false";
            const qPdComparativeValueWithStatistics =
              wizardForm.controls[`qPdComparative${suffix}`]?.value ===
                "true" &&
              wizardForm.controls[`qPdSummaryType${suffix}`]?.value === "true";

            return (
              <div
                key={`pd-block-${blockId}`}
                className="analysis-block"
                //style={{ marginBottom: "2rem" }}
              >
                {!isFirstBlock && (
                  <div
                    style={{
                      marginBottom: "1rem",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}>
                    <CButton
                      color={"danger"}
                      shape={"pill"}
                      data-tooltip-id="remove-pd-block"
                      onClick={e => handleRemoveBlock(e, displayIndex)}>
                      <BinIcon />
                    </CButton>
                    <Tooltip
                      id="remove-pd-block"
                      place="top"
                      delayShow={100}
                      className="custom-tooltip">
                      Remove this block
                    </Tooltip>
                  </div>
                )}

                <QuestionItem
                  questionIndex={questionIndex1a}
                  questionName={`qPdTable${suffix}`}
                  suffix={suffix}
                  activator={
                    wizardForm.controls.qPdStdEnabled?.value === "true" ||
                    wizardForm.controls.qPdCompEnabled?.value === "true"
                  }
                  formGroup={wizardForm}
                  isPlainSingleSelectDropdown={true}
                  globalId={globalId}
                />
                <QuestionItem
                  questionIndex={questionIndex1b}
                  questionName={`qPdPopulation${suffix}`}
                  suffix={suffix}
                  activator={
                    wizardForm.controls.qPdStdEnabled?.value === "true" ||
                    wizardForm.controls.qPdCompEnabled?.value === "true"
                  }
                  questionInputType={"text"}
                  globalId={globalId}
                />
                <QuestionItem
                  questionIndex={questionIndex1c}
                  questionName={`qPdComparative${suffix}`}
                  suffix={suffix}
                  activator={
                    wizardForm.controls.qPdStdEnabled?.value === "true" ||
                    wizardForm.controls.qPdCompEnabled?.value === "true"
                  }
                  questionInputType={"radio"}
                  questionInputChoice={"qPdComparative"}
                  globalId={globalId}
                />
                <QuestionItem
                  questionIndex={questionIndex1ci}
                  questionName={`qPdCompName${suffix}`}
                  suffix={suffix}
                  activator={qPdComparativeValue}
                  questionInputType={"text"}
                  globalId={globalId}
                />
                <QuestionItem
                  questionIndex={questionIndex1cii}
                  questionName={`qPdSummaryType${suffix}`}
                  suffix={suffix}
                  activator={qPdComparativeValue}
                  questionInputType={"radio"}
                  questionInputChoice={"yes_no"}
                  globalId={globalId}
                />
                <QuestionsAnswersArray
                  questionIndex={questionIndex1ciii}
                  controlName={`qPdParameters${suffix}`}
                  questionsName={`qPdParameters`}
                  activator={qPdComparativeValueWithStatistics}
                  formGroup={wizardForm}
                  inputTextBoxes={[["", "valueHelper", "text"]]}
                  globalId={globalId}
                />
                <QuestionsAnswersArray
                  questionIndex={questionIndex1ciii}
                  controlName={`qPdTreatmentGroups${suffix}`}
                  questionsName={`qPdTreatmentGroups`}
                  activator={qPdComparativeValueWithoutStatistics}
                  formGroup={wizardForm}
                  inputTextBoxes={[["", "valueHelper", "text"]]}
                  globalId={globalId}
                />
                <QuestionsAnswersArray
                  questionIndex={questionIndex1civ}
                  controlName={`qPdComparisonPairs${suffix}`}
                  questionsName={"qPdComparisonPairs"}
                  activator={qPdComparativeValueWithoutStatistics}
                  formGroup={wizardForm}
                  inputTextBoxes={[
                    ["Treatment", "Treatment", "select"],
                    ["Comparator", "Comparator", "select"],
                  ]}
                  inputTextBoxesAdditionalData={() =>
                    updatePdTreatmentGroupsComparisonAdditionalData(suffix)
                  }
                />
                <QuestionItem
                  questionIndex={questionIndex1cv}
                  questionName={`qPdDoseInteraction${suffix}`}
                  suffix={suffix}
                  activator={qPdComparativeValueWithoutStatistics}
                  questionInputType={"radio"}
                  questionInputChoice={"qPdDoseInteraction"}
                  globalId={globalId}
                />
                <QuestionsAnswersArray
                  questionIndex={questionIndex1ciNonComp}
                  controlName={`qPdColumnHeaders${suffix}`}
                  questionsName={`qPdColumnHeaders`}
                  activator={
                    wizardForm.controls[`qPdComparative${suffix}`]?.value !==
                      null && !qPdComparativeValue
                  }
                  formGroup={wizardForm}
                  inputTextBoxes={[["", "valueHelper", "text"]]}
                  globalId={globalId}
                />
                <QuestionsAnswersArray
                  questionIndex={questionIndex1d}
                  controlName={`qPdTimepoints${suffix}`}
                  questionsName={`qPdTimepoints`}
                  activator={qPdComparativeValueWithoutStatistics}
                  formGroup={wizardForm}
                  inputTextBoxes={[["", "valueHelper", "text"]]}
                  globalId={globalId}
                />
                <QuestionItem
                  questionIndex={questionIndex1e}
                  questionName={`qPdStatTables${suffix}`}
                  suffix={suffix}
                  activator={qPdComparativeValueWithoutStatistics}
                  formGroup={wizardForm}
                  isPlainMultiSelectDropdown={true}
                  tables={true}
                  globalId={globalId}
                />
              </div>
            );
          })}

          {(wizardForm.controls.qPdStdEnabled?.value === "true" ||
            wizardForm.controls.qPdCompEnabled?.value === "true") && (
            <div style={{ marginTop: "1rem", marginBottom: "2rem" }}>
              <CButton
                color={"primary"}
                variant={"outline"}
                className={"btn-light"}
                onClick={handleAddBlock}>
                Add another PD analysis <PlusIcon />
              </CButton>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default PDQuestions;
